import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faPencil,
  faTrash,
  faMagnifyingGlass,
  faShieldAlt,
  faExclamationTriangle,
  faLink,
  faPercent,
} from "@fortawesome/free-solid-svg-icons";
import api from "../services/api";
import InlineFieldTooltip from "../components/InlineFieldTooltip";

const CorrosionDamageManagement = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab");
  const validTabs = ["categories", "severity", "combinations", "damage-split"];
  const initialTab =
    tabFromUrl && validTabs.includes(tabFromUrl) ? tabFromUrl : "categories";

  // Active tab state
  const [activeTab, setActiveTab] = useState(initialTab);

  // Corrosion Categories state
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryFormData, setCategoryFormData] = useState({
    category_code: "",
    category_name: "",
    description: "",
  });
  const [categoryPagination, setCategoryPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0,
  });
  const [categorySearchTerm, setCategorySearchTerm] = useState("");

  // Damage Severity state
  const [severities, setSeverities] = useState([]);
  const [severitiesLoading, setSeveritiesLoading] = useState(true);
  const [showSeverityModal, setShowSeverityModal] = useState(false);
  const [editingSeverity, setEditingSeverity] = useState(null);
  const [severityFormData, setSeverityFormData] = useState({
    severity_code: "",
    severity_name: "",
    description: "",
  });
  const [severityPagination, setSeverityPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0,
  });
  const [severitySearchTerm, setSeveritySearchTerm] = useState("");

  // Combinations state
  const [combinations, setCombinations] = useState([]);
  const [combinationsLoading, setCombinationsLoading] = useState(true);
  const [combinationPagination, setCombinationPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0,
  });
  const [combinationSearchTerm, setCombinationSearchTerm] = useState("");
  const [corrosionCategoryFilter, setCorrosionCategoryFilter] = useState("");
  const [damageSeverityFilter, setDamageSeverityFilter] = useState("");
  const [corrosionCategories, setCorrosionCategories] = useState([]);
  const [damageSeverities, setDamageSeverities] = useState([]);

  // Damage Split Defaults state
  const [splits, setSplits] = useState([]);
  const [splitsLoading, setSplitsLoading] = useState(false);
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [editingSplit, setEditingSplit] = useState(null);
  const [splitFilters, setSplitFilters] = useState({ is_active: "" });
  const [splitFormData, setSplitFormData] = useState({
    corrosionCategory: "C2",
    severityA: 55,
    severityB: 35,
    severityC: 10,
    isActive: true,
  });

  const [error, setError] = useState(null);
  const [categoryAddWarning, setCategoryAddWarning] = useState(null);

  const tabs = [
    { id: "categories", name: "Exposure Class", icon: faShieldAlt },
    { id: "severity", name: "Damage Severity", icon: faExclamationTriangle },
    { id: "combinations", name: "Corrosion Damage Combinations", icon: faLink },
    { id: "damage-split", name: "Damage Split Defaults", icon: faPercent },
  ];

  // Fetch Corrosion Categories
  const fetchCategories = useCallback(async () => {
    try {
      setCategoriesLoading(true);
      const params = new URLSearchParams({
        page: categoryPagination.page,
        limit: categoryPagination.limit,
        ...(categorySearchTerm && { search: categorySearchTerm }),
      });

      const response = await api.get(`/corrosion-categories?${params}`);
      if (response.data.success) {
        setCategories(response.data.data);
        setCategoryPagination(response.data.pagination);
      }
    } catch (err) {
      setError("Failed to fetch exposure classes");
      console.error("Error fetching categories:", err);
    } finally {
      setCategoriesLoading(false);
    }
  }, [categoryPagination.page, categoryPagination.limit, categorySearchTerm]);

  // Fetch Damage Severities
  const fetchSeverities = useCallback(async () => {
    try {
      setSeveritiesLoading(true);
      const params = new URLSearchParams({
        page: severityPagination.page,
        limit: severityPagination.limit,
        ...(severitySearchTerm && { search: severitySearchTerm }),
      });

      const response = await api.get(`/damage-severity?${params}`);
      if (response.data.success) {
        setSeverities(response.data.data);
        setSeverityPagination(response.data.pagination);
      }
    } catch (err) {
      setError("Failed to fetch damage severities");
      console.error("Error fetching severities:", err);
    } finally {
      setSeveritiesLoading(false);
    }
  }, [severityPagination.page, severityPagination.limit, severitySearchTerm]);

  // Fetch Combinations (optional overrides for filter state when refetching after delete)
  const fetchCombinations = useCallback(
    async (overrides = {}) => {
      try {
        setCombinationsLoading(true);
        const categoryFilter =
          overrides.corrosionCategoryFilter !== undefined
            ? overrides.corrosionCategoryFilter
            : corrosionCategoryFilter;
        const severityFilter =
          overrides.damageSeverityFilter !== undefined
            ? overrides.damageSeverityFilter
            : damageSeverityFilter;
        const params = new URLSearchParams({
          page: combinationPagination.page,
          limit: combinationPagination.limit,
          ...(combinationSearchTerm && { search: combinationSearchTerm }),
          ...(categoryFilter && { corrosion_category_id: categoryFilter }),
          ...(severityFilter && { damage_severity_id: severityFilter }),
        });

        const response = await api.get(
          `/corrosion-damage-combinations?${params}`,
        );
        if (response.data.success) {
          setCombinations(response.data.data);
          setCombinationPagination(response.data.pagination);
        }
      } catch (err) {
        setError("Failed to fetch corrosion damage combinations");
        console.error("Error fetching combinations:", err);
      } finally {
        setCombinationsLoading(false);
      }
    },
    [
      corrosionCategoryFilter,
      damageSeverityFilter,
      combinationPagination.page,
      combinationPagination.limit,
      combinationSearchTerm,
    ],
  );

  // Fetch Damage Split Defaults
  const fetchSplits = useCallback(async () => {
    setSplitsLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      if (splitFilters.is_active !== "")
        params.append("is_active", splitFilters.is_active);
      const res = await api.get(`/damage-splits?${params.toString()}`);
      if (res.data.success) setSplits(res.data.data);
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to fetch damage split defaults",
      );
    } finally {
      setSplitsLoading(false);
    }
  }, [splitFilters.is_active]);

  // Fetch options for combination filters
  const fetchOptions = useCallback(async () => {
    try {
      const [categoriesResponse, severitiesResponse] = await Promise.all([
        api.get("/corrosion-damage-combinations/options/corrosion-categories"),
        api.get("/corrosion-damage-combinations/options/damage-severities"),
      ]);

      if (categoriesResponse.data.success) {
        setCorrosionCategories(categoriesResponse.data.data);
      }
      if (severitiesResponse.data.success) {
        setDamageSeverities(severitiesResponse.data.data);
      }
    } catch (err) {
      console.error("Error fetching options:", err);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "categories") {
      fetchCategories();
    } else if (activeTab === "severity") {
      fetchSeverities();
    } else if (activeTab === "combinations") {
      fetchCombinations();
    } else if (activeTab === "damage-split") {
      fetchSplits();
    }
  }, [
    activeTab,
    fetchCategories,
    fetchSeverities,
    fetchCombinations,
    fetchSplits,
  ]);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  useEffect(() => {
    setCategoryAddWarning(null);
  }, [activeTab]);

  // Category handlers
  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      const isNew = !editingCategory;
      if (editingCategory) {
        await api.put(
          `/corrosion-categories/${editingCategory.id}`,
          categoryFormData,
        );
      } else {
        await api.post("/corrosion-categories", categoryFormData);
      }

      setShowCategoryModal(false);
      setEditingCategory(null);
      setCategoryFormData({
        category_code: "",
        category_name: "",
        description: "",
      });
      fetchCategories();
      fetchOptions();
      if (isNew) {
        setCategoryAddWarning(
          "Remember to configure Damage Split Defaults for this exposure class in the Damage Split Defaults tab.",
        );
      }
    } catch (err) {
      setError("Failed to save exposure class");
      console.error("Error saving category:", err);
    }
  };

  const handleCategoryEdit = (category) => {
    setEditingCategory(category);
    setCategoryFormData({
      category_code: category.categoryCode,
      category_name: category.categoryName,
      description: category.description || "",
    });
    setShowCategoryModal(true);
  };

  const handleCategoryDelete = async (id) => {
    if (
      window.confirm("Are you sure you want to delete this exposure class?")
    ) {
      try {
        await api.delete(`/corrosion-categories/${id}`);
        if (String(corrosionCategoryFilter) === String(id)) {
          setCorrosionCategoryFilter("");
          if (activeTab === "combinations") {
            fetchCombinations({ corrosionCategoryFilter: "" });
          }
        }
        fetchCategories();
        fetchOptions();
      } catch (err) {
        setError("Failed to delete exposure class");
        console.error("Error deleting category:", err);
      }
    }
  };

  // Severity handlers
  const handleSeveritySubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSeverity) {
        await api.put(
          `/damage-severity/${editingSeverity.id}`,
          severityFormData,
        );
      } else {
        await api.post("/damage-severity", severityFormData);
      }

      setShowSeverityModal(false);
      setEditingSeverity(null);
      setSeverityFormData({
        severity_code: "",
        severity_name: "",
        description: "",
      });
      fetchSeverities();
      fetchOptions();
    } catch (err) {
      setError("Failed to save damage severity");
      console.error("Error saving severity:", err);
    }
  };

  const handleSeverityEdit = (severity) => {
    setEditingSeverity(severity);
    setSeverityFormData({
      severity_code: severity.severityCode,
      severity_name: severity.severityName,
      description: severity.description || "",
    });
    setShowSeverityModal(true);
  };

  const handleSeverityDelete = async (id) => {
    if (
      window.confirm("Are you sure you want to delete this damage severity?")
    ) {
      try {
        await api.delete(`/damage-severity/${id}`);
        if (String(damageSeverityFilter) === String(id)) {
          setDamageSeverityFilter("");
          if (activeTab === "combinations") {
            fetchCombinations({ damageSeverityFilter: "" });
          }
        }
        fetchSeverities();
        fetchOptions();
      } catch (err) {
        setError("Failed to delete damage severity");
        console.error("Error deleting severity:", err);
      }
    }
  };

  // Damage Split handlers
  const resetSplitForm = () => {
    const firstCode =
      corrosionCategories && corrosionCategories[0]?.categoryCode
        ? corrosionCategories[0].categoryCode
        : "C2";
    setSplitFormData({
      corrosionCategory: firstCode,
      severityA: 55,
      severityB: 35,
      severityC: 10,
      isActive: true,
    });
    setEditingSplit(null);
  };

  const handleSplitSubmit = async (e) => {
    e.preventDefault();
    setSplitsLoading(true);
    setError("");
    try {
      if (!editingSplit?.id) {
        setError("Select an existing damage split row and use Edit.");
        setSplitsLoading(false);
        return;
      }

      const total =
        Number(splitFormData.severityA) +
        Number(splitFormData.severityB) +
        Number(splitFormData.severityC);
      if (total !== 100) {
        setError("A+B+C must total 100%.");
        setSplitsLoading(false);
        return;
      }

      await api.put(`/damage-splits/${editingSplit.id}`, splitFormData);
      setShowSplitModal(false);
      resetSplitForm();
      fetchSplits();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save damage split");
    } finally {
      setSplitsLoading(false);
    }
  };

  const handleSplitEdit = (row) => {
    setEditingSplit(row);
    setSplitFormData({
      corrosionCategory: row.corrosionCategory,
      severityA: row.severityA,
      severityB: row.severityB,
      severityC: row.severityC,
      isActive: row.isActive,
    });
    setShowSplitModal(true);
  };

  const handleSplitDelete = async (row) => {
    if (!row?.id) return;

    if (!window.confirm(`Delete defaults for ${row.corrosionCategory}?`))
      return;
    try {
      await api.delete(`/damage-splits/${row.id}`);
      fetchSplits();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete damage split");
    }
  };

  const getSplitIsActive = (row) =>
    row.isActive !== undefined ? row.isActive : row.is_active !== false;

  const handleSplitToggleActive = async (row) => {
    if (!row?.id) {
      setError(
        "This exposure class has no saved split yet. Use Edit to set percentages and enable it.",
      );
      return;
    }

    try {
      await api.put(`/damage-splits/${row.id}`, {
        isActive: !getSplitIsActive(row),
      });
      fetchSplits();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update split status");
    }
  };

  const getOrderedSplitRows = (rows = []) => {
    const categoryOrder = (corrosionCategories || [])
      .map((category) => String(category.categoryCode || "").trim())
      .filter(Boolean);

    return [...rows].sort((a, b) => {
      const aCode = String(a.corrosionCategory || "");
      const bCode = String(b.corrosionCategory || "");
      const aIdx = categoryOrder.indexOf(aCode);
      const bIdx = categoryOrder.indexOf(bCode);
      if (aIdx !== -1 || bIdx !== -1) {
        const safeA = aIdx === -1 ? Number.MAX_SAFE_INTEGER : aIdx;
        const safeB = bIdx === -1 ? Number.MAX_SAFE_INTEGER : bIdx;
        if (safeA !== safeB) return safeA - safeB;
      }
      return aCode.localeCompare(bCode);
    });
  };

  const filteredSplitRows =
    splitFilters.is_active === ""
      ? splits
      : (splits || []).filter((row) =>
          splitFilters.is_active === "true"
            ? getSplitIsActive(row)
            : !getSplitIsActive(row),
        );

  const orderedSplitRows = getOrderedSplitRows(filteredSplitRows);

  // Get combination color based on corrosion category
  const getCombinationColor = (categoryCode) => {
    const colors = {
      C2: "bg-blue-100 text-blue-800",
      C3: "bg-green-100 text-green-800",
      C5: "bg-red-100 text-red-800",
    };
    return colors[categoryCode] || "bg-gray-100 text-gray-800";
  };

  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case "categories":
        return (
          <div className="bg-white rounded-lg shadow-sm border p-6">
            {/* Search and Add */}
            <div className="mb-4 flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <FontAwesomeIcon
                    icon={faMagnifyingGlass}
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4"
                  />
                  <input
                    type="text"
                    placeholder="Search exposure classes..."
                    value={categorySearchTerm}
                    onChange={(e) => setCategorySearchTerm(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <button
                onClick={() => {
                  setCategoryFormData({
                    category_code: "",
                    category_name: "",
                    description: "",
                  });
                  setEditingCategory(null);
                  setShowCategoryModal(true);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-2"
              >
                <FontAwesomeIcon icon={faPlus} className="h-4 w-4" />
                Add Exposure Class
              </button>
            </div>

            {/* Categories Table */}
            {categoriesLoading ? (
              <div className="p-6 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-gray-600">
                  Loading exposure classes...
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Exposure Class Code
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Exposure Class Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {categories.map((category) => (
                      <tr key={category.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {category.categoryCode}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {category.categoryName}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {category.description || "-"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleCategoryEdit(category)}
                              className="text-blue-600 hover:text-blue-900"
                              title="Edit"
                            >
                              <FontAwesomeIcon
                                icon={faPencil}
                                className="h-4 w-4"
                              />
                            </button>
                            <button
                              onClick={() => handleCategoryDelete(category.id)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete"
                            >
                              <FontAwesomeIcon
                                icon={faTrash}
                                className="h-4 w-4"
                              />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {activeTab === "categories" && categoryPagination.pages > 1 && (
              <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-4">
                <p className="text-sm text-gray-700">
                  Showing{" "}
                  {(categoryPagination.page - 1) * categoryPagination.limit + 1}{" "}
                  to{" "}
                  {Math.min(
                    categoryPagination.page * categoryPagination.limit,
                    categoryPagination.total,
                  )}{" "}
                  of {categoryPagination.total}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() =>
                      setCategoryPagination((p) => ({ ...p, page: p.page - 1 }))
                    }
                    disabled={categoryPagination.page === 1}
                    className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-1 text-sm">
                    Page {categoryPagination.page} of {categoryPagination.pages}
                  </span>
                  <button
                    onClick={() =>
                      setCategoryPagination((p) => ({ ...p, page: p.page + 1 }))
                    }
                    disabled={
                      categoryPagination.page === categoryPagination.pages
                    }
                    className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        );

      case "severity":
        return (
          <div className="bg-white rounded-lg shadow-sm border p-6">
            {/* Search and Add */}
            <div className="mb-4 flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <FontAwesomeIcon
                    icon={faMagnifyingGlass}
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4"
                  />
                  <input
                    type="text"
                    placeholder="Search severities..."
                    value={severitySearchTerm}
                    onChange={(e) => setSeveritySearchTerm(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <button
                onClick={() => {
                  setSeverityFormData({
                    severity_code: "",
                    severity_name: "",
                    description: "",
                  });
                  setEditingSeverity(null);
                  setShowSeverityModal(true);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-2"
              >
                <FontAwesomeIcon icon={faPlus} className="h-4 w-4" />
                Add Severity
              </button>
            </div>

            {/* Severities Table */}
            {severitiesLoading ? (
              <div className="p-6 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-gray-600">Loading severities...</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Severity Code
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Severity Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {severities.map((severity) => (
                      <tr key={severity.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {severity.severityCode}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {severity.severityName}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {severity.description || "-"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleSeverityEdit(severity)}
                              className="text-blue-600 hover:text-blue-900"
                              title="Edit"
                            >
                              <FontAwesomeIcon
                                icon={faPencil}
                                className="h-4 w-4"
                              />
                            </button>
                            <button
                              onClick={() => handleSeverityDelete(severity.id)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete"
                            >
                              <FontAwesomeIcon
                                icon={faTrash}
                                className="h-4 w-4"
                              />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {activeTab === "severity" && severityPagination.pages > 1 && (
              <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-4">
                <p className="text-sm text-gray-700">
                  Showing{" "}
                  {(severityPagination.page - 1) * severityPagination.limit + 1}{" "}
                  to{" "}
                  {Math.min(
                    severityPagination.page * severityPagination.limit,
                    severityPagination.total,
                  )}{" "}
                  of {severityPagination.total}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() =>
                      setSeverityPagination((p) => ({ ...p, page: p.page - 1 }))
                    }
                    disabled={severityPagination.page === 1}
                    className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-1 text-sm">
                    Page {severityPagination.page} of {severityPagination.pages}
                  </span>
                  <button
                    onClick={() =>
                      setSeverityPagination((p) => ({ ...p, page: p.page + 1 }))
                    }
                    disabled={
                      severityPagination.page === severityPagination.pages
                    }
                    className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        );

      case "combinations":
        return (
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> Combinations are automatically generated
                from Exposure Classes and Damage Severity levels. When you add
                or modify categories or severities, combinations are updated
                automatically.
              </p>
            </div>

            {/* Filters */}
            <div className="mb-4 flex flex-col lg:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <FontAwesomeIcon
                    icon={faMagnifyingGlass}
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4"
                  />
                  <input
                    type="text"
                    placeholder="Search combinations..."
                    value={combinationSearchTerm}
                    onChange={(e) => setCombinationSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 flex-1">
                <select
                  value={corrosionCategoryFilter}
                  onChange={(e) => setCorrosionCategoryFilter(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="">All Exposure Classes</option>
                  {corrosionCategories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.categoryCode}
                    </option>
                  ))}
                </select>
                <select
                  value={damageSeverityFilter}
                  onChange={(e) => setDamageSeverityFilter(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="">All Damage Severities</option>
                  {damageSeverities.map((severity) => (
                    <option key={severity.id} value={severity.id}>
                      {severity.severityCode}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Combinations Table - Description column removed */}
            {combinationsLoading ? (
              <div className="p-6 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-gray-600">Loading combinations...</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Combination Code
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Exposure Class
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Damage Severity
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {combinations.map((combination) => (
                      <tr key={combination.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getCombinationColor(combination.corrosionCategory?.categoryCode)}`}
                          >
                            {combination.combinationCode}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="flex items-center">
                            <FontAwesomeIcon
                              icon={faLink}
                              className="h-4 w-4 text-gray-400 mr-2"
                            />
                            {combination.corrosionCategory?.categoryCode} -{" "}
                            {combination.corrosionCategory?.categoryName}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="flex items-center">
                            <FontAwesomeIcon
                              icon={faLink}
                              className="h-4 w-4 text-gray-400 mr-2"
                            />
                            {combination.damageSeverity?.severityCode} -{" "}
                            {combination.damageSeverity?.severityName}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {activeTab === "combinations" &&
              combinationPagination.pages > 1 && (
                <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-4">
                  <p className="text-sm text-gray-700">
                    Showing{" "}
                    {(combinationPagination.page - 1) *
                      combinationPagination.limit +
                      1}{" "}
                    to{" "}
                    {Math.min(
                      combinationPagination.page * combinationPagination.limit,
                      combinationPagination.total,
                    )}{" "}
                    of {combinationPagination.total}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() =>
                        setCombinationPagination((p) => ({
                          ...p,
                          page: p.page - 1,
                        }))
                      }
                      disabled={combinationPagination.page === 1}
                      className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <span className="px-3 py-1 text-sm">
                      Page {combinationPagination.page} of{" "}
                      {combinationPagination.pages}
                    </span>
                    <button
                      onClick={() =>
                        setCombinationPagination((p) => ({
                          ...p,
                          page: p.page + 1,
                        }))
                      }
                      disabled={
                        combinationPagination.page ===
                        combinationPagination.pages
                      }
                      className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
          </div>
        );

      case "damage-split": {
        return (
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-md">
              <p className="text-amber-800 text-sm">
                All exposure classes are listed here automatically. New exposure
                classes appear as inactive with 0/0/0 until you edit and enable
                them.
              </p>
            </div>
            {/* Filters */}
            <div className="mb-4 p-4 bg-gray-50 rounded-lg border">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={splitFilters.is_active}
                    onChange={(e) =>
                      setSplitFilters({
                        ...splitFilters,
                        is_active: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All</option>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>

                <div className="flex items-end">
                  <button
                    onClick={() =>
                      setSplitFilters((prev) => ({ ...prev, is_active: "" }))
                    }
                    className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>

            {/* Table */}
            {splitsLoading ? (
              <div className="p-8 text-center text-gray-500">Loading...</div>
            ) : orderedSplitRows.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No damage split defaults found.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Exposure Class
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        A %
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        B %
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        C %
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Active
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {orderedSplitRows.map((row) => (
                      <tr
                        key={row.id || row.corrosionCategory}
                        className="hover:bg-gray-50"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-800">
                            {row.corrosionCategory}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {parseFloat(row.severityA).toFixed(0)}%
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {parseFloat(row.severityB).toFixed(0)}%
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {parseFloat(row.severityC).toFixed(0)}%
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            type="button"
                            role="switch"
                            aria-checked={getSplitIsActive(row)}
                            onClick={() => handleSplitToggleActive(row)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${getSplitIsActive(row) ? "bg-blue-600" : "bg-gray-300"}`}
                            title="Toggle active status"
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${getSplitIsActive(row) ? "translate-x-6" : "translate-x-1"}`}
                            />
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => handleSplitEdit(row)}
                            className="text-blue-600 hover:text-blue-900 mr-3"
                            title="Edit"
                          >
                            <FontAwesomeIcon icon={faPencil} />
                          </button>
                          <button
                            onClick={() => handleSplitDelete(row)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex items-start gap-2">
        <p className="text-gray-600">
          Manage exposure classes, damage severity levels, and view
          auto-generated combinations
        </p>
        <InlineFieldTooltip
          page="corrosion_damage"
          section="page_description"
          fieldKey="corrosion_damage_overview"
        />
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {categoryAddWarning && (
        <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-md flex items-start justify-between gap-3">
          <p className="text-amber-800 text-sm">{categoryAddWarning}</p>
          <button
            type="button"
            onClick={() => setCategoryAddWarning(null)}
            className="text-amber-600 hover:text-amber-800 shrink-0"
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="mb-8">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === tab.id
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
                onClick={() => {
                  setActiveTab(tab.id);
                  setSearchParams({ tab: tab.id });
                }}
              >
                <FontAwesomeIcon icon={tab.icon} className="h-4 w-4" />
                <span>{tab.name}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="mb-8">{renderTabContent()}</div>

      {/* Category Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="flex items-center gap-2 text-lg font-medium text-gray-900 mb-4">
                <span>
                  {editingCategory
                    ? "Edit Exposure Class"
                    : "Add New Exposure Class"}
                </span>
                <InlineFieldTooltip
                  page="corrosion_damage"
                  section="modal_titles"
                  fieldKey="corrosion_category_form"
                  fallbackText="Create or edit exposure classes."
                  title="Exposure Class Form"
                />
              </h3>
              <form onSubmit={handleCategorySubmit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Exposure Class Code *
                  </label>
                  <input
                    type="text"
                    required
                    value={categoryFormData.category_code}
                    onChange={(e) =>
                      setCategoryFormData({
                        ...categoryFormData,
                        category_code: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., C2, C3, C5"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Exposure Class Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={categoryFormData.category_name}
                    onChange={(e) =>
                      setCategoryFormData({
                        ...categoryFormData,
                        category_name: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Exposure Class Mild"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={categoryFormData.description}
                    onChange={(e) =>
                      setCategoryFormData({
                        ...categoryFormData,
                        description: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="3"
                    placeholder="Optional description"
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCategoryModal(false);
                      setEditingCategory(null);
                      setCategoryFormData({
                        category_code: "",
                        category_name: "",
                        description: "",
                      });
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {editingCategory ? "Update" : "Create"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Severity Modal */}
      {showSeverityModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="flex items-center gap-2 text-lg font-medium text-gray-900 mb-4">
                <span>
                  {editingSeverity
                    ? "Edit Damage Severity"
                    : "Add New Damage Severity"}
                </span>
                <InlineFieldTooltip
                  page="corrosion_damage"
                  section="modal_titles"
                  fieldKey="damage_severity_form"
                  fallbackText="Create or edit damage severity definitions."
                  title="Damage Severity Form"
                />
              </h3>
              <form onSubmit={handleSeveritySubmit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Severity Code *
                  </label>
                  <input
                    type="text"
                    required
                    value={severityFormData.severity_code}
                    onChange={(e) =>
                      setSeverityFormData({
                        ...severityFormData,
                        severity_code: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., A, B, C"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Severity Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={severityFormData.severity_name}
                    onChange={(e) =>
                      setSeverityFormData({
                        ...severityFormData,
                        severity_name: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Minor, Moderate, Severe"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={severityFormData.description}
                    onChange={(e) =>
                      setSeverityFormData({
                        ...severityFormData,
                        description: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="3"
                    placeholder="Optional description"
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowSeverityModal(false);
                      setEditingSeverity(null);
                      setSeverityFormData({
                        severity_code: "",
                        severity_name: "",
                        description: "",
                      });
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {editingSeverity ? "Update" : "Create"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Damage Split Modal */}
      {showSplitModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="flex items-center gap-2 text-lg font-medium text-gray-900 mb-4">
                <span>
                  {editingSplit ? "Edit Damage Split" : "Add Damage Split"}
                </span>
                <InlineFieldTooltip
                  page="corrosion_damage"
                  section="modal_titles"
                  fieldKey="damage_split_form"
                  fallbackText="Create or edit default damage split percentages."
                  title="Damage Split Form"
                />
              </h3>
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-red-800 text-sm">{error}</p>
                </div>
              )}
              <form onSubmit={handleSplitSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Exposure Class *
                    </label>
                    <select
                      value={splitFormData.corrosionCategory}
                      onChange={(e) =>
                        setSplitFormData({
                          ...splitFormData,
                          corrosionCategory: e.target.value,
                        })
                      }
                      required
                      disabled={!!editingSplit}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                    >
                      {(corrosionCategories && corrosionCategories.length > 0
                        ? corrosionCategories
                        : []
                      ).map((cat) => (
                        <option key={cat.id} value={cat.categoryCode}>
                          {cat.categoryCode}
                        </option>
                      ))}
                      {(!corrosionCategories ||
                        corrosionCategories.length === 0) && (
                        <option value="">
                          No exposure classes — add some in Exposure Class tab
                        </option>
                      )}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {["A", "B", "C"].map((k) => (
                    <div key={k}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Severity {k} (%) *
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="1"
                        value={splitFormData[`severity${k}`]}
                        onChange={(e) =>
                          setSplitFormData({
                            ...splitFormData,
                            [`severity${k}`]: e.target.value,
                          })
                        }
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  ))}
                </div>

                <p className="text-xs text-gray-500">
                  Total:{" "}
                  {Number(splitFormData.severityA) +
                    Number(splitFormData.severityB) +
                    Number(splitFormData.severityC)}
                  %
                </p>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={splitFormData.isActive}
                    onChange={(e) =>
                      setSplitFormData({
                        ...splitFormData,
                        isActive: e.target.checked,
                      })
                    }
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 text-sm text-gray-700">Active</label>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowSplitModal(false);
                      resetSplitForm();
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={splitsLoading}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {splitsLoading
                      ? "Saving..."
                      : editingSplit
                        ? "Update"
                        : "Create"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CorrosionDamageManagement;
